/* Le decisioni prese in assenza di prescrizione, e la loro storia.
 *
 * **La cronologia e' raggiungibile da ogni riga.** Non e' un dettaglio
 * accessorio: alla prima consegna respinta la domanda e' quale soglia fosse in
 * vigore quel giorno, e una schermata che mostrasse solo il valore corrente
 * costringerebbe a cercare quella risposta nella base dati.
 *
 * **La motivazione e' obbligatoria dove la decisione pesa**, e quali pesino lo
 * dice il servizio col campo `critical`. La pagina non tiene un proprio elenco
 * di chiavi critiche: una regola scritta due volte diventa due regole, e la
 * seconda si scopre il giorno in cui divergono.
 *
 * **Si aggiunge una versione, non si modifica un valore.** Il tasto dice
 * «registra una versione» perche' e' quello che succede: il valore precedente
 * resta, con chi lo aveva scelto.
 */
import { useEffect, useState } from 'react'

import { ServiceError } from '../../api'
import {
  addSettingVersion,
  readSettingHistory,
  readSettings,
  readValue,
  SOURCES,
  sourceLabel,
  writeValue,
  type Setting,
  type SettingVersion,
} from '../../admin/settings'
import { Hint } from '../../carta/Hint'
import { StatusPill } from '../../carta/StatusPill'
import { moment } from '../../format'
import { useLocale, useT } from '../../i18n/LocaleProvider'
import { t as translate } from '../../i18n/t'

export function SettingsAdmin(): JSX.Element {
  const t = useT()
  const { locale } = useLocale()
  const [settings, setSettings] = useState<Setting[] | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const [open, setOpen] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void readSettings()
      .then((found) => {
        if (!cancelled) setSettings(found)
      })
      .catch((error: unknown) => {
        if (!cancelled) setFailure(read(error, translate(locale, 'settings.readFailed')))
      })
    return () => {
      cancelled = true
    }
  }, [locale])

  async function refresh(): Promise<void> {
    setSettings(await readSettings())
  }

  const chosen = settings?.find((one) => one.key === open)

  return (
    <main className="amministrazione">
      <h2>{t('title.settings')}</h2>
      {failure !== null && <p role="alert">{failure}</p>}
      {settings === null ? (
        <p role="status">{t('settings.loading')}</p>
      ) : (
        <>
          <p className="tenue">{t('settings.intro')}</p>
          <table>
            <thead>
              <tr>
                <th scope="col">{t('settings.colDecision')}</th>
                <th scope="col">{t('settings.colValue')}</th>
                <th scope="col">{t('settings.colSource')}</th>
                <th scope="col">{t('settings.colVersion')}</th>
                <th scope="col">{t('settings.colHistory')}</th>
              </tr>
            </thead>
            <tbody>
              {settings.map((setting) => (
                <tr key={setting.key}>
                  <th scope="row">
                    <code>{setting.key}</code>
                    {setting.critical && (
                      <>
                        {' '}
                        <StatusPill label={t('settings.critical')} tone="sev-4" />
                        <Hint about="critical" />
                      </>
                    )}
                    {setting.is_hypothesis && <span className="tenue">{t('settings.hypothesis')}</span>}
                  </th>
                  <td>
                    <code>{writeValue(setting.value)}</code>
                  </td>
                  <td>{sourceLabel(setting.source)}</td>
                  <td>{setting.version === 0 ? t('common.none') : `v${setting.version}`}</td>
                  <td>
                    <button
                      type="button"
                      aria-expanded={open === setting.key}
                      onClick={() => setOpen(open === setting.key ? null : setting.key)}
                    >
                      {open === setting.key ? t('common.close') : t('settings.openHistory')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {chosen !== undefined && (
            // `key` sulla chiave della decisione: passando da una decisione
            // all'altra il modulo si azzera invece di portarsi dietro la
            // motivazione scritta per la precedente.
            <SettingDetail key={chosen.key} setting={chosen} onWritten={() => void refresh()} />
          )}
        </>
      )}
    </main>
  )
}

function SettingDetail({
  setting,
  onWritten,
}: {
  setting: Setting
  onWritten: () => void
}): JSX.Element {
  const t = useT()
  const { locale } = useLocale()
  const [history, setHistory] = useState<SettingVersion[] | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  // Le caselle partono dal valore in vigore: chi cambia una soglia parte quasi
  // sempre da quella, e riscriverla a memoria e' il modo di sbagliarla.
  const [value, setValue] = useState(writeValue(setting.value))
  const [source, setSource] = useState(setting.source)
  const [reason, setReason] = useState('')
  const [questionRef, setQuestionRef] = useState(setting.question_ref ?? '')

  const key = setting.key

  useEffect(() => {
    let cancelled = false
    void readSettingHistory(key)
      .then((found) => {
        if (!cancelled) setHistory(found)
      })
      .catch((error: unknown) => {
        if (!cancelled) setFailure(read(error, translate(locale, 'settings.historyFailed')))
      })
    return () => {
      cancelled = true
    }
  }, [key, locale])

  function onSubmit(event: React.FormEvent): void {
    event.preventDefault()
    setBusy(true)
    setFailure(null)
    void (async () => {
      try {
        await addSettingVersion(setting.key, {
          value: readValue(value),
          source,
          reason: reason.trim() === '' ? null : reason.trim(),
          question_ref: questionRef.trim() === '' ? null : questionRef.trim(),
          question: setting.question,
          rationale: setting.rationale,
          confidence: setting.confidence,
        })
        setHistory(await readSettingHistory(setting.key))
        setReason('')
        onWritten()
      } catch (error: unknown) {
        if (error instanceof SyntaxError) {
          // Un valore che non e' JSON scriverebbe `"true"` dove serve `true`, e
          // la differenza si scoprirebbe alla prima consegna convalidata col
          // valore sbagliato.
          setFailure(t('settings.invalidJson'))
        } else {
          setFailure(read(error, t('settings.versionFailed')))
        }
      } finally {
        setBusy(false)
      }
    })()
  }

  const reasonMissing = setting.critical && reason.trim() === ''

  return (
    <section className="storia-decisione" aria-labelledby="storia-titolo">
      <h3 id="storia-titolo">
        <code>{setting.key}</code>
      </h3>
      {setting.question !== null && <p>{setting.question}</p>}
      {setting.rationale !== null && <p className="tenue">{setting.rationale}</p>}

      {failure !== null && <p role="alert">{failure}</p>}

      <h4>{t('settings.historyTitle')}</h4>
      {history === null ? (
        <p role="status">{t('settings.historyLoading')}</p>
      ) : history.length === 0 ? (
        <p>{t('settings.noVersions')}</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th scope="col">{t('settings.colVersion')}</th>
              <th scope="col">{t('settings.colValue')}</th>
              <th scope="col">{t('settings.colSource')}</th>
              <th scope="col">{t('settings.colReason')}</th>
              <th scope="col">{t('settings.colDecidedBy')}</th>
              <th scope="col">{t('settings.colWhen')}</th>
            </tr>
          </thead>
          <tbody>
            {history.map((one) => (
              <tr key={one.version}>
                <th scope="row">v{one.version}</th>
                <td>
                  <code>{writeValue(one.value)}</code>
                </td>
                <td>{sourceLabel(one.source)}</td>
                <td>{one.reason ?? <span className="tenue">{t('common.dash')}</span>}</td>
                <td>{one.decided_by ?? <span className="tenue">{t('common.dash')}</span>}</td>
                <td>{moment(one.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h4>{t('settings.registerTitle')}</h4>
      <form onSubmit={onSubmit}>
        <label>
          {t('settings.valueJson')}
          <input value={value} onChange={(event) => setValue(event.target.value)} required />
        </label>
        <label>
          {t('settings.source')}
          <Hint about="hypothesis" />
          <select value={source} onChange={(event) => setSource(event.target.value)}>
            {SOURCES.map((one) => (
              <option key={one.value} value={one.value}>
                {sourceLabel(one.value)}
              </option>
            ))}
          </select>
        </label>
        {source === 'hypothesis' && (
          <label>
            {t('settings.questionRef')}
            <input
              value={questionRef}
              onChange={(event) => setQuestionRef(event.target.value)}
              maxLength={16}
              placeholder="Q7"
            />
          </label>
        )}
        <label>
          {setting.critical ? t('settings.reasonRequired') : t('settings.reasonOptional')}
          <textarea
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            maxLength={1024}
            required={setting.critical}
          />
        </label>
        {setting.critical && <p className="tenue">{t('settings.criticalNote')}</p>}
        <button type="submit" className="primario" disabled={busy || reasonMissing}>
          {t('settings.submit')}
        </button>
      </form>
    </section>
  )
}

function read(error: unknown, fallback: string): string {
  return error instanceof ServiceError ? error.message : fallback
}
